import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, ChevronLeftIcon, PencilIcon, TrashIcon,
  ClipboardDocumentListIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';

const fmtD  = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}).replace(/ /g,'-') : '—';
const fmtDT = d => d ? new Date(d).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';
const ini   = (n='') => n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'S';

const STATUS_COLORS = {
  draft:        { bg:'#f0f2f7', color:'#6b7194' },
  active:       { bg:'#ecfdf5', color:'#10c98e' },
  under_review: { bg:'#fff7ed', color:'#f97316' },
  completed:    { bg:'#eef2ff', color:'#3b6ef8' },
  discontinued: { bg:'#fef2f2', color:'#ef4444' },
};
const GOAL_STATUS_COLORS = {
  not_started:  { bg:'#f0f2f7', color:'#6b7194' },
  in_progress:  { bg:'#fff7ed', color:'#f97316' },
  achieved:     { bg:'#ecfdf5', color:'#10c98e' },
  discontinued: { bg:'#fef2f2', color:'#ef4444' },
};
const RATING_COLORS = {
  excellent:         { bg:'#ecfdf5', color:'#10c98e' },
  good:              { bg:'#eef2ff', color:'#3b6ef8' },
  satisfactory:      { bg:'#f3f0ff', color:'#8b5cf6' },
  needs_improvement: { bg:'#fff7ed', color:'#f97316' },
  concern:           { bg:'#fef2f2', color:'#ef4444' },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ti*{box-sizing:border-box;}
  .ti{font-family:'Sora',sans-serif;color:#1a1d2e;animation:tiFade .4s ease both;}
  @keyframes tiFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .ti-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .ti-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .ti-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 16px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;flex-shrink:0;}
  .btn-blue{background:#3b6ef8;color:white;} .btn-blue:hover{box-shadow:0 4px 14px rgba(59,110,248,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-red{background:#fef2f2;color:#ef4444;border:1px solid #fecaca;} .btn-red:hover{background:#ef4444;color:white;}
  .btn-sm{padding:5px 12px;font-size:.74rem;} .btn:disabled{opacity:.5;cursor:not-allowed;}
  .card{background:white;border-radius:14px;border:1px solid #e8ebf4;padding:18px 20px;margin-bottom:12px;cursor:pointer;transition:.15s;}
  .card:hover{border-color:#3b6ef8;box-shadow:0 2px 12px rgba(59,110,248,.08);}
  .card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:10px;}
  .card-name{font-weight:700;font-size:.92rem;color:#0f1123;}
  .card-meta{font-size:.72rem;color:#9094a8;margin-top:2px;}
  .pill{display:inline-flex;font-size:.67rem;font-weight:700;padding:3px 10px;border-radius:99px;}
  .av{width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#3b6ef8,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:.68rem;font-weight:700;color:white;flex-shrink:0;}
  .progress-bar-wrap{height:5px;background:#f0f2f7;border-radius:99px;overflow:hidden;margin-top:8px;}
  .progress-bar{height:100%;background:linear-gradient(90deg,#3b6ef8,#8b5cf6);border-radius:99px;transition:width .4s;}
  .empty{text-align:center;padding:52px 20px;color:#b0b5cc;font-size:.82rem;}
  .spin{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#3b6ef8;border-radius:50%;animation:sp .7s linear infinite;display:inline-block;}
  @keyframes sp{to{transform:rotate(360deg)}}
  .detail{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;}
  .detail-hdr{padding:20px 24px;border-bottom:1px solid #f0f2f8;display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px;}
  .detail-tabs{display:flex;gap:4px;padding:12px 16px;background:#f8f9fc;border-bottom:1px solid #f0f2f8;overflow-x:auto;}
  .dtab{padding:7px 16px;border-radius:9px;border:none;font-family:'Sora',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;background:transparent;color:#9094a8;white-space:nowrap;transition:.15s;}
  .dtab.on{background:white;color:#0f1123;box-shadow:0 1px 4px rgba(0,0,0,.1);}
  .detail-body{padding:24px;}
  .section-title{font-size:.72rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;margin-bottom:12px;}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:20px;}
  .info-item{display:flex;flex-direction:column;gap:4px;}
  .info-lbl{font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .info-val{font-size:.82rem;color:#1a1d2e;font-weight:500;}
  .goal-card{background:#f8f9fc;border-radius:12px;border:1px solid #e8ebf4;padding:14px 16px;margin-bottom:10px;}
  .goal-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:8px;}
  .goal-desc{font-size:.83rem;font-weight:600;color:#1a1d2e;line-height:1.5;}
  .goal-detail{font-size:.76rem;color:#6b7194;margin-top:4px;line-height:1.55;}
  .update-card{border-left:3px solid #3b6ef8;padding:12px 16px;margin-bottom:12px;background:#fafbff;border-radius:0 10px 10px 0;}
  .update-meta{font-size:.7rem;color:#9094a8;margin-bottom:6px;}
  .update-note{font-size:.82rem;color:#1a1d2e;line-height:1.55;}
  .comment-card{background:#f8f9fc;border-radius:12px;padding:12px 14px;margin-bottom:10px;border:1px solid #e8ebf4;}
  .comment-meta{font-size:.7rem;color:#9094a8;margin-bottom:5px;}
  .comment-text{font-size:.82rem;color:#1a1d2e;}
  .reply-box{background:#eef2ff;border-radius:8px;padding:10px 12px;margin-top:8px;font-size:.78rem;color:#3b6ef8;}
  .reply-form{margin-top:8px;display:flex;gap:8px;}
  .reply-input{flex:1;font-family:'Sora',sans-serif;font-size:.78rem;padding:7px 10px;border:1.5px solid #e0e4f0;border-radius:8px;outline:none;background:white;}
  .reply-input:focus{border-color:#3b6ef8;}
  .overlay{position:fixed;inset:0;top:0;left:0;right:0;bottom:0;width:100vw;height:100vh;background:rgba(10,12,28,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:99999;padding:20px;}
  .modal{background:white;border-radius:20px;width:100%;max-width:680px;max-height:92vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;animation:mIn .25s ease;}
  @keyframes mIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
  .m-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .m-hdr-l{display:flex;align-items:center;gap:10px;}
  .m-icon{width:34px;height:34px;border-radius:10px;background:#eef2ff;color:#3b6ef8;display:flex;align-items:center;justify-content:center;}
  .m-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .m-title{font-size:.95rem;font-weight:700;color:#0f1123;} .m-sub{font-size:.72rem;color:#9094a8;}
  .m-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;}
  .m-close svg{width:14px;height:14px;stroke-width:2;}
  .m-body{padding:22px;overflow-y:auto;flex:1;}
  .m-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap;}
  .fg{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .ff{display:flex;flex-direction:column;gap:5px;} .ff.full{grid-column:1/-1;}
  .fl{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .fi,.fta,.fsel{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;transition:.15s;}
  .fi:focus,.fta:focus,.fsel:focus{border-color:#3b6ef8;background:white;box-shadow:0 0 0 3px rgba(59,110,248,.08);}
  .fta{resize:vertical;min-height:72px;}
  .fdiv{grid-column:1/-1;display:flex;align-items:center;gap:10px;margin:6px 0;}
  .fdiv-lbl{font-size:.65rem;font-weight:700;color:#c0c5dc;text-transform:uppercase;letter-spacing:.09em;white-space:nowrap;}
  .fdiv-line{flex:1;height:1px;background:#f0f2f8;}
  .goal-edit-card{background:#f8f9fc;border-radius:12px;border:1px solid #e8ebf4;padding:14px;margin-bottom:10px;}
  .goal-edit-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;}
  .goal-num{font-size:.72rem;font-weight:700;color:#9094a8;}
  @media(max-width:768px){.ti-title{font-size:1.2rem;}.fg{grid-template-columns:1fr;}.ff.full{grid-column:1;}.fdiv{grid-column:1;}.info-grid{grid-template-columns:1fr;}}
`;

const FD = ({ label }) => (
  <div className="fdiv"><span className="fdiv-lbl">{label}</span><div className="fdiv-line" /></div>
);

const emptyGoal = { area:'', type:'short_term', description:'', strategy:'', targetDate:'', status:'not_started', successCriteria:'' };
const emptyForm = {
  studentId:'', academicYear: `${new Date().getFullYear()}-${new Date().getFullYear()+1}`,
  category:'underperforming', categoryNote:'',
  currentLevel:{ strengths:'', weaknesses:'', learningGaps:'', learningStyle:'', assessmentBasis:'' },
  goals:[{ ...emptyGoal }],
  supportStrategies:'', accommodations:'', resourcesRequired:'',
  reviewFrequency:'monthly', nextReviewDate:'',
  studentInvolved:false, visibleToParent:true, status:'draft',
  startDate: new Date().toISOString().split('T')[0], endDate:'',
};

const goalsDone = (goals=[]) => goals.filter(g => g.status === 'achieved').length;

// ─── Extracted components (defined OUTSIDE main component to prevent re-mount blinking) ──

const ILPCard = ({ ilp, onClick }) => {
  const sc    = STATUS_COLORS[ilp.status] || STATUS_COLORS.draft;
  const done  = goalsDone(ilp.goals);
  const total = ilp.goals?.length || 0;
  const pct   = total ? Math.round(done / total * 100) : 0;
  const name  = ilp.student?.user?.name || 'Unknown Student';
  return (
    <div className="card" onClick={onClick}>
      <div className="card-top">
        <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
          <div className="av">{ini(name)}</div>
          <div>
            <div className="card-name">{name}</div>
            <div className="card-meta">{ilp.student?.class?.name} · {ilp.student?.section} · Adm: {ilp.student?.admissionNo}</div>
            <div className="card-meta" style={{marginTop:3}}>{ilp.academicYear} · <span style={{textTransform:'capitalize'}}>{(ilp.category||'').replace('_',' ')}</span></div>
          </div>
        </div>
        <span className="pill" style={{background:sc.bg,color:sc.color,flexShrink:0}}>{ilp.status?.replace('_',' ')}</span>
      </div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:'.72rem',color:'#9094a8'}}>
        <span>Goals: {done}/{total} achieved</span>
        <span>{ilp.progressUpdates?.length||0} updates · {ilp.parentComments?.length||0} comments</span>
      </div>
      {total > 0 && <div className="progress-bar-wrap"><div className="progress-bar" style={{width:pct+'%'}}/></div>}
    </div>
  );
};

const GoalEditCard = ({ g, idx, total, onRemove, onSet }) => (
  <div className="goal-edit-card">
    <div className="goal-edit-hdr">
      <span className="goal-num">Goal {idx+1}</span>
      {total > 1 && <button type="button" onClick={onRemove} style={{background:'none',border:'none',cursor:'pointer',color:'#ef4444',fontSize:'.72rem',fontWeight:600}}>Remove</button>}
    </div>
    <div className="fg" style={{gap:10}}>
      <div className="ff"><label className="fl">Area</label><input className="fi" placeholder="Math / Reading / Behavior…" value={g.area} onChange={e=>onSet('area',e.target.value)}/></div>
      <div className="ff">
        <label className="fl">Type</label>
        <select className="fsel" value={g.type} onChange={e=>onSet('type',e.target.value)}>
          <option value="short_term">Short Term</option>
          <option value="long_term">Long Term</option>
        </select>
      </div>
      <div className="ff full"><label className="fl">Description *</label><textarea className="fta" style={{minHeight:56}} value={g.description} onChange={e=>onSet('description',e.target.value)} placeholder="What is the goal?" required/></div>
      <div className="ff full"><label className="fl">Strategy</label><textarea className="fta" style={{minHeight:56}} value={g.strategy} onChange={e=>onSet('strategy',e.target.value)} placeholder="How will this goal be achieved?"/></div>
      <div className="ff full"><label className="fl">Success Criteria</label><input className="fi" value={g.successCriteria} onChange={e=>onSet('successCriteria',e.target.value)} placeholder="How will we know the goal is met?"/></div>
      <div className="ff"><label className="fl">Target Date</label><input type="date" className="fi" value={g.targetDate} onChange={e=>onSet('targetDate',e.target.value)}/></div>
      <div className="ff">
        <label className="fl">Status</label>
        <select className="fsel" value={g.status} onChange={e=>onSet('status',e.target.value)}>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="achieved">Achieved</option>
          <option value="discontinued">Discontinued</option>
        </select>
      </div>
    </div>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────
export default function TeacherILP() {
  const [view,       setView]       = useState('list');
  const [ilps,       setIlps]       = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [detailTab,  setDetailTab]  = useState('overview');
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const [progForm,   setProgForm]   = useState({ note:'', overallRating:'', goalRefs:[] });
  const [students,   setStudents]   = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [replyBoxes, setReplyBoxes] = useState({});
  const [filter,     setFilter]     = useState({ status:'', search:'' });

  const loadIlps = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/teacher/ilp');
      setIlps(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load ILPs'); }
    finally { setLoading(false); }
  }, []);

  const loadStudents = useCallback(async () => {
    try {
      const { data } = await api.get('/teacher/classes');
      // Handle both { classes: [...] } and plain array
      const classes = Array.isArray(data) ? data : (data?.classes || []);
      // Flatten all students from all classes
      const seen = new Set();
      const all  = [];
      classes.forEach(cls => {
        (cls.students || []).forEach(s => {
          const id = s._id || s.studentId;
          if (id && !seen.has(String(id))) {
            seen.add(String(id));
            all.push({
              _id:         id,
              admissionNo: s.admissionNo,
              user:        s.user || { name: s.name },
              class:       { name: cls.name },
              section:     s.section,
            });
          }
        });
      });
      // If classes don't embed students, try the students endpoint
      if (all.length === 0) {
        try {
          const r2 = await api.get('/teacher/classes');
          const classList = Array.isArray(r2.data) ? r2.data : (r2.data?.classes || []);
          for (const cls of classList) {
            for (const section of (cls.sections || [])) {
              try {
                const r3 = await api.get(`/teacher/student-attendance?classId=${cls._id}&section=${section}&date=${new Date().toISOString().split('T')[0]}`);
                (r3.data?.students || []).forEach(s => {
                  if (!seen.has(String(s.studentId))) {
                    seen.add(String(s.studentId));
                    all.push({ _id: s.studentId, admissionNo: s.admissionNo, user: { name: s.name }, class: { name: cls.name }, section });
                  }
                });
              } catch {}
            }
          }
        } catch {}
      }
      setStudents(all);
    } catch { setStudents([]); }
  }, []);

  useEffect(() => { loadIlps(); loadStudents(); }, [loadIlps, loadStudents]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/teacher/ilp/${id}`);
      setSelected(data); setView('detail'); setDetailTab('overview');
    } catch { toast.error('Failed to load ILP'); }
  };

  const openCreate = () => { setForm({ ...emptyForm, goals:[{ ...emptyGoal }] }); setModal('create'); };

  const openEdit = () => {
    const i = selected;
    setForm({
      studentId: i.student?._id || '', academicYear: i.academicYear || '',
      category: i.category || 'underperforming', categoryNote: i.categoryNote || '',
      currentLevel: { strengths:'', weaknesses:'', learningGaps:'', learningStyle:'', assessmentBasis:'', ...i.currentLevel },
      goals: (i.goals||[]).map(g => ({ ...emptyGoal, ...g, targetDate: g.targetDate ? new Date(g.targetDate).toISOString().split('T')[0] : '' })),
      supportStrategies: i.supportStrategies||'', accommodations: i.accommodations||'', resourcesRequired: i.resourcesRequired||'',
      reviewFrequency: i.reviewFrequency||'monthly', nextReviewDate: i.nextReviewDate ? new Date(i.nextReviewDate).toISOString().split('T')[0] : '',
      studentInvolved: !!i.studentInvolved, visibleToParent: i.visibleToParent !== false, status: i.status||'draft',
      startDate: i.startDate ? new Date(i.startDate).toISOString().split('T')[0] : '',
      endDate:   i.endDate   ? new Date(i.endDate).toISOString().split('T')[0]   : '',
    });
    setModal('edit');
  };

  const addGoal    = () => setForm(p => ({ ...p, goals: [...p.goals, { ...emptyGoal }] }));
  const removeGoal = (idx) => setForm(p => ({ ...p, goals: p.goals.filter((_,i) => i !== idx) }));
  const setGoal    = (idx, field, val) => setForm(p => {
    const goals = [...p.goals]; goals[idx] = { ...goals[idx], [field]: val }; return { ...p, goals };
  });
  const setCL = (field, val) => setForm(p => ({ ...p, currentLevel: { ...p.currentLevel, [field]: val } }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, goals: form.goals.filter(g => g.description.trim()) };
      if (modal === 'create') { await api.post('/teacher/ilp', payload); toast.success('ILP created'); setModal(null); loadIlps(); }
      else { await api.put(`/teacher/ilp/${selected._id}`, payload); toast.success('ILP updated'); setModal(null); openDetail(selected._id); }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const submitProgress = async () => {
    if (!progForm.note.trim()) return toast.error('Note is required');
    setSaving(true);
    try {
      await api.post(`/teacher/ilp/${selected._id}/progress`, progForm);
      toast.success('Progress update added');
      setModal(null); setProgForm({ note:'', overallRating:'', goalRefs:[] }); openDetail(selected._id);
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const submitReply = async (commentId) => {
    const text = replyBoxes[commentId];
    if (!text?.trim()) return;
    try {
      await api.post(`/teacher/ilp/${selected._id}/reply/${commentId}`, { reply: text });
      toast.success('Reply sent');
      setReplyBoxes(p => ({ ...p, [commentId]: '' })); openDetail(selected._id);
    } catch { toast.error('Failed'); }
  };

  const deleteIlp = async () => {
    if (!window.confirm('Delete this draft ILP?')) return;
    try { await api.delete(`/teacher/ilp/${selected._id}`); toast.success('Deleted'); setView('list'); setSelected(null); loadIlps(); }
    catch (err) { toast.error(err.response?.data?.message || 'Can only delete drafts'); }
  };

  const filtered = ilps.filter(i => {
    if (filter.status && i.status !== filter.status) return false;
    if (filter.search) { const q = filter.search.toLowerCase(); return i.student?.user?.name?.toLowerCase().includes(q) || i.student?.admissionNo?.toLowerCase().includes(q); }
    return true;
  });

  // ── LIST VIEW ─────────────────────────────────────────────────────────────────
  if (view === 'list') return (
    <div className="ti">
      <style>{css}</style>
      <div className="ti-hdr">
        <div><div className="ti-title">Individual Learning Plans</div><div className="ti-sub">Create and manage ILPs for your students.</div></div>
        <button className="btn btn-blue" onClick={openCreate}><PlusIcon/>New ILP</button>
      </div>
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <input className="fi" style={{maxWidth:220,padding:'8px 12px'}} placeholder="Search student…" value={filter.search} onChange={e=>setFilter(p=>({...p,search:e.target.value}))}/>
        <select className="fsel" style={{maxWidth:160,padding:'8px 12px'}} value={filter.status} onChange={e=>setFilter(p=>({...p,status:e.target.value}))}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="discontinued">Discontinued</option>
          <option value="draft">Draft</option>
          <option value="under_review">Under Review</option>
        </select>
      </div>
      {loading
        ? <div style={{textAlign:'center',padding:'48px',color:'#9094a8',fontSize:'.82rem'}}><span className="spin"/> Loading…</div>
        : !filtered.length
          ? <div className="empty">No ILPs found. Create one to get started.</div>
          : filtered.map(i => <ILPCard key={i._id} ilp={i} onClick={() => openDetail(i._id)} />)
      }
      {modal === 'create' && createPortal(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal">
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon"><ClipboardDocumentListIcon/></div>
                <div><div className="m-title">New ILP</div><div className="m-sub">Create an Individual Learning Plan</div></div>
              </div>
              <button className="m-close" onClick={()=>setModal(null)}><XMarkIcon/></button>
            </div>
            <form onSubmit={submit} style={{display:'contents'}}>
              <div className="m-body">
                <div className="fg">
                  <FD label="Student & Year"/>
                  <div className="ff full">
                    <label className="fl">Student</label>
                    <select className="fsel" value={form.studentId} onChange={e=>setForm(p=>({...p,studentId:e.target.value}))} required>
                      <option value="">— Select Student —</option>
                      {students.map(s => <option key={s._id} value={s._id}>{s.user?.name} ({s.admissionNo}){s.class?.name ? ` — ${s.class.name}` : ''}{s.section ? ` ${s.section}` : ''}</option>)}
                    </select>
                    {students.length === 0 && <span style={{fontSize:'.72rem',color:'#f97316',marginTop:4}}>No students found. Make sure you have assigned classes.</span>}
                  </div>
                  <div className="ff"><label className="fl">Academic Year</label><input className="fi" placeholder="2025-2026" value={form.academicYear} onChange={e=>setForm(p=>({...p,academicYear:e.target.value}))} required/></div>
                  <div className="ff">
                    <label className="fl">Category</label>
                    <select className="fsel" value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                      <option value="learning_difficulty">Learning Difficulty</option>
                      <option value="underperforming">Underperforming</option>
                      <option value="at_risk">At Risk</option>
                      <option value="gifted">Gifted</option>
                      <option value="behavioral">Behavioral</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="ff full"><label className="fl">Category Note</label><input className="fi" placeholder="Additional context…" value={form.categoryNote} onChange={e=>setForm(p=>({...p,categoryNote:e.target.value}))}/></div>
                  <FD label="Current Level Assessment"/>
                  <div className="ff full"><label className="fl">Strengths</label><textarea className="fta" value={form.currentLevel.strengths} onChange={e=>setCL('strengths',e.target.value)} placeholder="What is the student good at?"/></div>
                  <div className="ff full"><label className="fl">Weaknesses / Challenges</label><textarea className="fta" value={form.currentLevel.weaknesses} onChange={e=>setCL('weaknesses',e.target.value)} placeholder="Areas of difficulty…"/></div>
                  <div className="ff full"><label className="fl">Learning Gaps</label><textarea className="fta" value={form.currentLevel.learningGaps} onChange={e=>setCL('learningGaps',e.target.value)} placeholder="Specific gaps identified…"/></div>
                  <div className="ff"><label className="fl">Learning Style</label><input className="fi" placeholder="Visual / Auditory / Kinesthetic…" value={form.currentLevel.learningStyle} onChange={e=>setCL('learningStyle',e.target.value)}/></div>
                  <div className="ff"><label className="fl">Assessment Basis</label><input className="fi" placeholder="How was this determined?" value={form.currentLevel.assessmentBasis} onChange={e=>setCL('assessmentBasis',e.target.value)}/></div>
                  <FD label="Goals"/>
                  <div className="ff full">
                    {form.goals.map((g, idx) => (
                      <GoalEditCard key={idx} g={g} idx={idx} total={form.goals.length}
                        onRemove={() => removeGoal(idx)}
                        onSet={(field, val) => setGoal(idx, field, val)} />
                    ))}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={addGoal}><PlusIcon/>Add Goal</button>
                  </div>
                  <FD label="Support & Resources"/>
                  <div className="ff full"><label className="fl">Support Strategies</label><textarea className="fta" value={form.supportStrategies} onChange={e=>setForm(p=>({...p,supportStrategies:e.target.value}))} placeholder="General strategies…"/></div>
                  <div className="ff full"><label className="fl">Accommodations</label><textarea className="fta" value={form.accommodations} onChange={e=>setForm(p=>({...p,accommodations:e.target.value}))} placeholder="Extra time, front seat, large print…"/></div>
                  <div className="ff full"><label className="fl">Resources Required</label><textarea className="fta" value={form.resourcesRequired} onChange={e=>setForm(p=>({...p,resourcesRequired:e.target.value}))} placeholder="Materials, tools, specialist support…"/></div>
                  <FD label="Timeline & Review"/>
                  <div className="ff"><label className="fl">Start Date</label><input type="date" className="fi" value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}/></div>
                  <div className="ff"><label className="fl">End Date</label><input type="date" className="fi" value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}/></div>
                  <div className="ff">
                    <label className="fl">Review Frequency</label>
                    <select className="fsel" value={form.reviewFrequency} onChange={e=>setForm(p=>({...p,reviewFrequency:e.target.value}))}>
                      <option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option><option value="termly">Termly</option>
                    </select>
                  </div>
                  <div className="ff"><label className="fl">Next Review Date</label><input type="date" className="fi" value={form.nextReviewDate} onChange={e=>setForm(p=>({...p,nextReviewDate:e.target.value}))}/></div>
                  <FD label="Settings"/>
                  <div className="ff">
                    <label className="fl">Status</label>
                    <select className="fsel" value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                      <option value="draft">Draft</option><option value="active">Active</option>
                      <option value="under_review">Under Review</option><option value="completed">Completed</option>
                      <option value="discontinued">Discontinued</option>
                    </select>
                  </div>
                  <div className="ff" style={{justifyContent:'flex-end',gap:12}}>
                    <label style={{display:'flex',alignItems:'center',gap:8,fontSize:'.8rem',fontWeight:500,cursor:'pointer'}}>
                      <input type="checkbox" checked={form.visibleToParent} onChange={e=>setForm(p=>({...p,visibleToParent:e.target.checked}))} style={{accentColor:'#3b6ef8'}}/>Visible to parent
                    </label>
                    <label style={{display:'flex',alignItems:'center',gap:8,fontSize:'.8rem',fontWeight:500,cursor:'pointer'}}>
                      <input type="checkbox" checked={form.studentInvolved} onChange={e=>setForm(p=>({...p,studentInvolved:e.target.checked}))} style={{accentColor:'#3b6ef8'}}/>Student involved
                    </label>
                  </div>
                </div>
              </div>
              <div className="m-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-blue" disabled={saving}>{saving?'Saving…':'Create ILP'}</button>
              </div>
            </form>
          </div>
        </div>
      , document.body)}
    </div>
  );

  // ── DETAIL VIEW ───────────────────────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const sc   = STATUS_COLORS[selected.status] || STATUS_COLORS.draft;
    const name = selected.student?.user?.name || 'Unknown';
    const done = goalsDone(selected.goals);
    const total = selected.goals?.length || 0;
    const pct  = total ? Math.round(done / total * 100) : 0;

    return (
      <div className="ti">
        <style>{css}</style>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>{setView('list');setSelected(null);}}><ChevronLeftIcon/>Back</button>
          <div style={{flex:1}}/>
          {selected.status==='draft' && <button className="btn btn-red btn-sm" onClick={deleteIlp}><TrashIcon/>Delete</button>}
          <button className="btn btn-ghost btn-sm" onClick={()=>{setProgForm({note:'',overallRating:'',goalRefs:[]});setModal('progress');}}><ArrowPathIcon/>Add Progress</button>
          <button className="btn btn-blue btn-sm" onClick={openEdit}><PencilIcon/>Edit ILP</button>
        </div>

        <div className="detail">
          <div className="detail-hdr">
            <div style={{display:'flex',gap:14,alignItems:'flex-start'}}>
              <div className="av" style={{width:44,height:44,fontSize:'.8rem'}}>{ini(name)}</div>
              <div>
                <div style={{fontWeight:700,fontSize:'1.05rem',color:'#0f1123'}}>{name}</div>
                <div style={{fontSize:'.76rem',color:'#9094a8',marginTop:2}}>{selected.student?.class?.name} · Section {selected.student?.section} · {selected.student?.admissionNo}</div>
                <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
                  <span className="pill" style={{background:sc.bg,color:sc.color}}>{selected.status?.replace('_',' ')}</span>
                  <span className="pill" style={{background:'#f3f0ff',color:'#8b5cf6',textTransform:'capitalize'}}>{(selected.category||'').replace('_',' ')}</span>
                  <span className="pill" style={{background:'#f0f2f7',color:'#6b7194'}}>{selected.academicYear}</span>
                </div>
              </div>
            </div>
            <div style={{textAlign:'right',fontSize:'.72rem',color:'#9094a8'}}>
              <div>Created by {selected.createdBy?.name}</div>
              <div style={{marginTop:3}}>Last updated {fmtD(selected.updatedAt)}</div>
              {selected.parentAcknowledged && <div style={{marginTop:3,color:'#10c98e',fontWeight:600}}>✓ Parent acknowledged {fmtD(selected.parentAcknowledgedAt)}</div>}
            </div>
          </div>

          <div style={{padding:'12px 24px',background:'#f8f9fc',borderBottom:'1px solid #f0f2f8',display:'flex',alignItems:'center',gap:14}}>
            <div style={{fontSize:'.76rem',color:'#6b7194',fontWeight:600,flexShrink:0}}>Goals: {done}/{total} achieved ({pct}%)</div>
            <div className="progress-bar-wrap" style={{flex:1,margin:0}}><div className="progress-bar" style={{width:pct+'%'}}/></div>
          </div>

          <div className="detail-tabs">
            {['overview','goals','progress','comments'].map(t => (
              <button key={t} className={`dtab${detailTab===t?' on':''}`} onClick={()=>setDetailTab(t)} style={{textTransform:'capitalize'}}>{t}</button>
            ))}
          </div>

          <div className="detail-body">
            {detailTab === 'overview' && (
              <div>
                <div className="section-title">Plan Details</div>
                <div className="info-grid">
                  <div className="info-item"><div className="info-lbl">Start Date</div><div className="info-val">{fmtD(selected.startDate)}</div></div>
                  <div className="info-item"><div className="info-lbl">End Date</div><div className="info-val">{fmtD(selected.endDate)}</div></div>
                  <div className="info-item"><div className="info-lbl">Review Frequency</div><div className="info-val" style={{textTransform:'capitalize'}}>{selected.reviewFrequency}</div></div>
                  <div className="info-item"><div className="info-lbl">Next Review</div><div className="info-val">{fmtD(selected.nextReviewDate)}</div></div>
                  <div className="info-item"><div className="info-lbl">Student Involved</div><div className="info-val">{selected.studentInvolved?'Yes':'No'}</div></div>
                  <div className="info-item"><div className="info-lbl">Visible to Parent</div><div className="info-val">{selected.visibleToParent?'Yes':'No'}</div></div>
                </div>
                <div className="section-title" style={{marginTop:8}}>Current Level Assessment</div>
                {['strengths','weaknesses','learningGaps','learningStyle','assessmentBasis'].map(f => (
                  selected.currentLevel?.[f] && (
                    <div key={f} style={{marginBottom:12}}>
                      <div className="info-lbl" style={{marginBottom:4,textTransform:'capitalize'}}>{f.replace(/([A-Z])/g,' $1')}</div>
                      <div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4'}}>{selected.currentLevel[f]}</div>
                    </div>
                  )
                ))}
                {selected.supportStrategies && <><div className="section-title" style={{marginTop:8}}>Support Strategies</div><div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4',marginBottom:12}}>{selected.supportStrategies}</div></>}
                {selected.accommodations    && <><div className="section-title">Accommodations</div><div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4',marginBottom:12}}>{selected.accommodations}</div></>}
                {selected.resourcesRequired && <><div className="section-title">Resources Required</div><div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4'}}>{selected.resourcesRequired}</div></>}
              </div>
            )}

            {detailTab === 'goals' && (
              <div>
                <div className="section-title">Goals ({selected.goals?.length||0})</div>
                {!(selected.goals?.length) && <div style={{color:'#b0b5cc',fontSize:'.82rem'}}>No goals set yet.</div>}
                {(selected.goals||[]).map((g, idx) => {
                  const gs = GOAL_STATUS_COLORS[g.status] || GOAL_STATUS_COLORS.not_started;
                  return (
                    <div key={g._id||idx} className="goal-card">
                      <div className="goal-top">
                        <div>
                          <div style={{fontSize:'.67rem',fontWeight:700,color:'#9094a8',marginBottom:4,textTransform:'uppercase'}}>{g.area||'General'} · <span style={{textTransform:'capitalize'}}>{g.type?.replace('_',' ')}</span></div>
                          <div className="goal-desc">{g.description}</div>
                        </div>
                        <span className="pill" style={{background:gs.bg,color:gs.color,flexShrink:0,textTransform:'capitalize'}}>{g.status?.replace('_',' ')}</span>
                      </div>
                      {g.strategy        && <div className="goal-detail"><strong>Strategy:</strong> {g.strategy}</div>}
                      {g.successCriteria && <div className="goal-detail"><strong>Success criteria:</strong> {g.successCriteria}</div>}
                      {g.targetDate      && <div className="goal-detail"><strong>Target date:</strong> {fmtD(g.targetDate)}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {detailTab === 'progress' && (
              <div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                  <div className="section-title" style={{margin:0}}>Progress Updates ({selected.progressUpdates?.length||0})</div>
                  <button className="btn btn-blue btn-sm" onClick={()=>{setProgForm({note:'',overallRating:'',goalRefs:[]});setModal('progress');}}><PlusIcon/>Add Update</button>
                </div>
                {!(selected.progressUpdates?.length) && <div style={{color:'#b0b5cc',fontSize:'.82rem'}}>No progress updates yet.</div>}
                {[...(selected.progressUpdates||[])].reverse().map((u, idx) => {
                  const rc = RATING_COLORS[u.overallRating];
                  return (
                    <div key={u._id||idx} className="update-card">
                      <div className="update-meta">
                        <strong>{u.updatedBy?.name||'Teacher'}</strong> · {fmtDT(u.date||u.createdAt)}
                        {rc && <span className="pill" style={{...rc,marginLeft:8,textTransform:'capitalize'}}>{u.overallRating?.replace('_',' ')}</span>}
                      </div>
                      <div className="update-note">{u.note}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {detailTab === 'comments' && (
              <div>
                <div className="section-title">Parent Comments ({selected.parentComments?.length||0})</div>
                {!(selected.parentComments?.length) && <div style={{color:'#b0b5cc',fontSize:'.82rem'}}>No comments from parents yet.</div>}
                {(selected.parentComments||[]).map((cm, idx) => (
                  <div key={cm._id||idx} className="comment-card">
                    <div className="comment-meta"><strong>{cm.parent?.name||'Parent'}</strong> · {fmtDT(cm.date||cm.createdAt)}</div>
                    <div className="comment-text">{cm.comment}</div>
                    {cm.reply
                      ? <div className="reply-box"><strong>Your reply:</strong> {cm.reply} <span style={{color:'#9094a8',fontWeight:400}}>· {fmtD(cm.repliedAt)}</span></div>
                      : <div className="reply-form">
                          <input className="reply-input" placeholder="Write a reply…" value={replyBoxes[cm._id]||''} onChange={e=>setReplyBoxes(p=>({...p,[cm._id]:e.target.value}))}/>
                          <button className="btn btn-blue btn-sm" onClick={()=>submitReply(cm._id)}>Reply</button>
                        </div>
                    }
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Edit modal in detail view */}
        {modal === 'edit' && createPortal(
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
            <div className="modal">
              <div className="m-hdr">
                <div className="m-hdr-l">
                  <div className="m-icon"><ClipboardDocumentListIcon/></div>
                  <div><div className="m-title">Edit ILP</div><div className="m-sub">Update the ILP details</div></div>
                </div>
                <button className="m-close" onClick={()=>setModal(null)}><XMarkIcon/></button>
              </div>
              <form onSubmit={submit} style={{display:'contents'}}>
                <div className="m-body">
                  <div className="fg">
                    <FD label="Student & Year"/>
                    <div className="ff full"><label className="fl">Student</label><input className="fi" value={selected?.student?.user?.name||''} disabled style={{opacity:.7}}/></div>
                    <div className="ff"><label className="fl">Academic Year</label><input className="fi" value={form.academicYear} onChange={e=>setForm(p=>({...p,academicYear:e.target.value}))} required/></div>
                    <div className="ff">
                      <label className="fl">Category</label>
                      <select className="fsel" value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                        <option value="learning_difficulty">Learning Difficulty</option><option value="underperforming">Underperforming</option>
                        <option value="at_risk">At Risk</option><option value="gifted">Gifted</option>
                        <option value="behavioral">Behavioral</option><option value="other">Other</option>
                      </select>
                    </div>
                    <div className="ff full"><label className="fl">Category Note</label><input className="fi" value={form.categoryNote} onChange={e=>setForm(p=>({...p,categoryNote:e.target.value}))}/></div>
                    <FD label="Current Level Assessment"/>
                    <div className="ff full"><label className="fl">Strengths</label><textarea className="fta" value={form.currentLevel.strengths} onChange={e=>setCL('strengths',e.target.value)}/></div>
                    <div className="ff full"><label className="fl">Weaknesses</label><textarea className="fta" value={form.currentLevel.weaknesses} onChange={e=>setCL('weaknesses',e.target.value)}/></div>
                    <div className="ff full"><label className="fl">Learning Gaps</label><textarea className="fta" value={form.currentLevel.learningGaps} onChange={e=>setCL('learningGaps',e.target.value)}/></div>
                    <div className="ff"><label className="fl">Learning Style</label><input className="fi" value={form.currentLevel.learningStyle} onChange={e=>setCL('learningStyle',e.target.value)}/></div>
                    <div className="ff"><label className="fl">Assessment Basis</label><input className="fi" value={form.currentLevel.assessmentBasis} onChange={e=>setCL('assessmentBasis',e.target.value)}/></div>
                    <FD label="Goals"/>
                    <div className="ff full">
                      {form.goals.map((g, idx) => (
                        <GoalEditCard key={idx} g={g} idx={idx} total={form.goals.length}
                          onRemove={() => removeGoal(idx)}
                          onSet={(field, val) => setGoal(idx, field, val)} />
                      ))}
                      <button type="button" className="btn btn-ghost btn-sm" onClick={addGoal}><PlusIcon/>Add Goal</button>
                    </div>
                    <FD label="Support & Resources"/>
                    <div className="ff full"><label className="fl">Support Strategies</label><textarea className="fta" value={form.supportStrategies} onChange={e=>setForm(p=>({...p,supportStrategies:e.target.value}))}/></div>
                    <div className="ff full"><label className="fl">Accommodations</label><textarea className="fta" value={form.accommodations} onChange={e=>setForm(p=>({...p,accommodations:e.target.value}))}/></div>
                    <div className="ff full"><label className="fl">Resources Required</label><textarea className="fta" value={form.resourcesRequired} onChange={e=>setForm(p=>({...p,resourcesRequired:e.target.value}))}/></div>
                    <FD label="Timeline & Review"/>
                    <div className="ff"><label className="fl">Start Date</label><input type="date" className="fi" value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}/></div>
                    <div className="ff"><label className="fl">End Date</label><input type="date" className="fi" value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}/></div>
                    <div className="ff">
                      <label className="fl">Review Frequency</label>
                      <select className="fsel" value={form.reviewFrequency} onChange={e=>setForm(p=>({...p,reviewFrequency:e.target.value}))}>
                        <option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option><option value="termly">Termly</option>
                      </select>
                    </div>
                    <div className="ff"><label className="fl">Next Review Date</label><input type="date" className="fi" value={form.nextReviewDate} onChange={e=>setForm(p=>({...p,nextReviewDate:e.target.value}))}/></div>
                    <FD label="Settings"/>
                    <div className="ff">
                      <label className="fl">Status</label>
                      <select className="fsel" value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                        <option value="draft">Draft</option><option value="active">Active</option>
                        <option value="under_review">Under Review</option><option value="completed">Completed</option>
                        <option value="discontinued">Discontinued</option>
                      </select>
                    </div>
                    <div className="ff" style={{justifyContent:'flex-end',gap:12}}>
                      <label style={{display:'flex',alignItems:'center',gap:8,fontSize:'.8rem',fontWeight:500,cursor:'pointer'}}>
                        <input type="checkbox" checked={form.visibleToParent} onChange={e=>setForm(p=>({...p,visibleToParent:e.target.checked}))} style={{accentColor:'#3b6ef8'}}/>Visible to parent
                      </label>
                      <label style={{display:'flex',alignItems:'center',gap:8,fontSize:'.8rem',fontWeight:500,cursor:'pointer'}}>
                        <input type="checkbox" checked={form.studentInvolved} onChange={e=>setForm(p=>({...p,studentInvolved:e.target.checked}))} style={{accentColor:'#3b6ef8'}}/>Student involved
                      </label>
                    </div>
                  </div>
                </div>
                <div className="m-foot">
                  <button type="button" className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-blue" disabled={saving}>{saving?'Saving…':'Update ILP'}</button>
                </div>
              </form>
            </div>
          </div>
        , document.body)}

        {/* Progress modal */}
        {modal === 'progress' && createPortal(
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
            <div className="modal" style={{maxWidth:480}}>
              <div className="m-hdr">
                <div className="m-hdr-l">
                  <div className="m-icon"><ArrowPathIcon/></div>
                  <div><div className="m-title">Add Progress Update</div><div className="m-sub">{selected?.student?.user?.name}</div></div>
                </div>
                <button className="m-close" onClick={()=>setModal(null)}><XMarkIcon/></button>
              </div>
              <div className="m-body">
                <div className="fg">
                  <div className="ff full">
                    <label className="fl">Progress Note *</label>
                    <textarea className="fta" rows={4} value={progForm.note} onChange={e=>setProgForm(p=>({...p,note:e.target.value}))} placeholder="Describe the student's progress this period…"/>
                  </div>
                  <div className="ff full">
                    <label className="fl">Overall Rating</label>
                    <select className="fsel" value={progForm.overallRating} onChange={e=>setProgForm(p=>({...p,overallRating:e.target.value}))}>
                      <option value="">— Select Rating —</option>
                      <option value="excellent">Excellent</option><option value="good">Good</option>
                      <option value="satisfactory">Satisfactory</option><option value="needs_improvement">Needs Improvement</option>
                      <option value="concern">Concern</option>
                    </select>
                  </div>
                  <div className="ff full">
                    <label className="fl">Related Goals (optional)</label>
                    <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:140,overflowY:'auto'}}>
                      {(selected?.goals||[]).map(g => (
                        <label key={g._id} style={{display:'flex',alignItems:'flex-start',gap:8,fontSize:'.78rem',cursor:'pointer',background:'#f8f9fc',padding:'6px 10px',borderRadius:8,border:'1px solid #e8ebf4'}}>
                          <input type="checkbox" style={{marginTop:2,accentColor:'#3b6ef8'}}
                            checked={progForm.goalRefs.includes(String(g._id))}
                            onChange={e => { const id=String(g._id); setProgForm(p=>({...p,goalRefs:e.target.checked?[...p.goalRefs,id]:p.goalRefs.filter(r=>r!==id)})); }}/>
                          <span>{g.description}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="m-foot">
                <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
                <button className="btn btn-blue" onClick={submitProgress} disabled={saving}>{saving?'Saving…':'Add Update'}</button>
              </div>
            </div>
          </div>
        , document.body)}
      </div>
    );
  }

  return null;
}